import React from 'react';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Spinner,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { CurrencyDollar, TrendUp, Clock } from 'phosphor-react';
import { useEventos, IEventoFinanceiro } from '../../../hooks/useEventos';
import { IDataNoPagination } from '../../../types/shared';
import { formatCurrency } from '../../../utils/FormatFunctions';

export default function FinanceiroDashboard() {
  const { getFinanceiro } = useEventos();

  const { data: finData, isFetching } = useQuery<IDataNoPagination<IEventoFinanceiro>>({
    queryKey: ['eventos-financeiro'],
    queryFn: getFinanceiro,
  });

  const fin = finData?.data;

  if (isFetching) {
    return (
      <Flex justify="center" py={16}>
        <Spinner size="lg" color="gray.300" thickness="3px" />
      </Flex>
    );
  }

  if (!fin) return null;

  const realizadoPct = fin.orcamento_total > 0 ? (fin.realizado / fin.orcamento_total) * 100 : 0;

  const kpis = [
    {
      label: 'Orçamento Total',
      value: fin.orcamento_total,
      icon: CurrencyDollar,
      color: '#3B82F6',
      bg: '#EFF6FF',
    },
    {
      label: 'Realizado',
      value: fin.realizado,
      icon: TrendUp,
      color: '#22C55E',
      bg: '#F0FDF4',
    },
    {
      label: 'Pendente',
      value: fin.pendente,
      icon: Clock,
      color: '#EF4444',
      bg: '#FEF2F2',
    },
  ];

  return (
    <Box>
      {/* KPI Cards */}
      <SimpleGrid columns={3} spacing={4} mb={6}>
        {kpis.map(kpi => (
          <Box
            key={kpi.label}
            bg="white"
            borderRadius="16px"
            border="1px solid"
            borderColor="gray.100"
            p={5}
            boxShadow="0 1px 2px rgba(0,0,0,0.04)"
          >
            <Flex align="center" gap={3} mb={3}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="10px"
                bg={kpi.bg}
                align="center"
                justify="center"
              >
                <kpi.icon size={18} color={kpi.color} weight="bold" />
              </Flex>
              <Text
                fontSize="11px"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {kpi.label}
              </Text>
            </Flex>
            <Text
              fontSize="28px"
              fontWeight="800"
              color="gray.800"
              letterSpacing="-0.03em"
              lineHeight="1"
            >
              {formatCurrency(kpi.value)}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      {/* Progress geral */}
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid"
        borderColor="gray.100"
        p={5}
        mb={6}
        boxShadow="0 1px 2px rgba(0,0,0,0.04)"
      >
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="13px" fontWeight="600" color="gray.600">
            Progresso Geral
          </Text>
          <Text fontSize="13px" fontWeight="700" color="gray.700">
            {Math.round(realizadoPct)}%
          </Text>
        </Flex>
        <Progress
          value={realizadoPct}
          size="sm"
          borderRadius="full"
          colorScheme={realizadoPct >= 100 ? 'green' : 'blue'}
          bg="gray.100"
        />
      </Box>

      <Flex gap={5} direction={{ base: 'column', lg: 'row' }}>
        {/* Por Categoria */}
        <Box
          flex={1}
          bg="white"
          borderRadius="16px"
          border="1px solid"
          borderColor="gray.100"
          p={5}
          boxShadow="0 1px 2px rgba(0,0,0,0.04)"
        >
          <Text fontSize="14px" fontWeight="700" color="gray.800" mb={4}>
            Por Categoria
          </Text>
          {fin.por_categoria.map(cat => {
            const pct = cat.valor_total > 0 ? (cat.valor_pago / cat.valor_total) * 100 : 0;
            return (
              <Box
                key={cat.categoria_nome}
                mb={4}
                p={3}
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.50"
                bg="gray.25"
                borderLeft="3px solid"
                borderLeftColor={cat.categoria_cor || 'gray.300'}
              >
                <Flex justify="space-between" mb={1.5}>
                  <HStack spacing={2}>
                    <Text fontSize="13px" fontWeight="600" color="gray.700">
                      {cat.categoria_nome}
                    </Text>
                  </HStack>
                  <Text fontSize="13px" fontWeight="700" color="gray.700">
                    {formatCurrency(cat.valor_total)}
                  </Text>
                </Flex>
                <Progress
                  value={pct}
                  size="xs"
                  borderRadius="full"
                  colorScheme={pct >= 100 ? 'green' : 'blue'}
                  bg="gray.100"
                  mb={1.5}
                />
                <Text fontSize="11px" color="gray.400">
                  {formatCurrency(cat.valor_pago)} pago · {cat.total_contratos} contrato
                  {cat.total_contratos !== 1 ? 's' : ''}
                </Text>
              </Box>
            );
          })}
          {fin.por_categoria.length === 0 && (
            <Text fontSize="13px" color="gray.400" textAlign="center" py={4}>
              Sem dados
            </Text>
          )}
        </Box>

        {/* Por Evento */}
        <Box
          flex={1}
          bg="white"
          borderRadius="16px"
          border="1px solid"
          borderColor="gray.100"
          p={5}
          boxShadow="0 1px 2px rgba(0,0,0,0.04)"
        >
          <Text fontSize="14px" fontWeight="700" color="gray.800" mb={4}>
            Por Evento
          </Text>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th
                    fontSize="10px"
                    fontWeight="600"
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    borderColor="gray.100"
                  >
                    Evento
                  </Th>
                  <Th
                    fontSize="10px"
                    fontWeight="600"
                    color="gray.400"
                    textTransform="uppercase"
                    isNumeric
                    borderColor="gray.100"
                  >
                    Orçado
                  </Th>
                  <Th
                    fontSize="10px"
                    fontWeight="600"
                    color="gray.400"
                    textTransform="uppercase"
                    isNumeric
                    borderColor="gray.100"
                  >
                    Pago
                  </Th>
                  <Th
                    fontSize="10px"
                    fontWeight="600"
                    color="gray.400"
                    textTransform="uppercase"
                    isNumeric
                    borderColor="gray.100"
                  >
                    Pend.
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {fin.por_evento.map(ev => (
                  <Tr key={ev.evento_id} _hover={{ bg: 'gray.50' }}>
                    <Td fontSize="12px" fontWeight="500" color="gray.700" borderColor="gray.50">
                      {ev.evento_nome}
                    </Td>
                    <Td fontSize="12px" isNumeric color="gray.500" borderColor="gray.50">
                      {formatCurrency(ev.valor_orcado)}
                    </Td>
                    <Td
                      fontSize="12px"
                      isNumeric
                      color="green.500"
                      fontWeight="600"
                      borderColor="gray.50"
                    >
                      {formatCurrency(ev.valor_pago)}
                    </Td>
                    <Td
                      fontSize="12px"
                      isNumeric
                      color={ev.valor_pendente > 0 ? 'red.400' : 'gray.400'}
                      borderColor="gray.50"
                    >
                      {formatCurrency(ev.valor_pendente)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          {fin.por_evento.length === 0 && (
            <Text fontSize="13px" color="gray.400" textAlign="center" py={4}>
              Sem dados
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
